import type { Probe, Finding, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { lookupTlsSignature } from '../data/tlsSignatures';
import { cfNumber, cfString, type EdgePayload } from '../edge/payload';

const JA4_SOURCE = 'https://github.com/FoxIO-LLC/ja4';

/**
 * Edge probe — relays request.cf via the tiny Cloudflare Worker.
 *
 * This is the ONLY probe with a backend dependency, and it degrades cleanly: if
 * the Worker is not deployed, unreachable, or blocked, every signal reports
 * 'unavailable' and the rest of the scan is unaffected. That is what keeps the
 * site a pure static deploy.
 */
export const edgeProbe: Probe = {
  id: 'edge',
  label: 'Network edge (TLS/transport)',
  layer: 'tls',
  tier: 'edge',
  category: 'network',
  description: 'Cloudflare edge metadata: TLS ClientHello hashes, protocol, geo, and ASN',
  budgetMs: 6000,

  async run(): Promise<ProbeResult> {
    const endpoint = resolveEndpoint();
    if (!endpoint) {
      return {
        signals: {
          // value=false, not null: "we checked and it is not available" is a
          // real observation, distinct from "we could not determine".
          available: ok(false, 'client'),
          note: na<string>('unavailable', 'client', 'no edge endpoint configured (VITE_EDGE_ENDPOINT)'),
        },
      };
    }

    let payload: EdgePayload;
    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 5000);
      const res = await fetch(endpoint, { signal: controller.signal, cache: 'no-store' });
      clearTimeout(timer);
      if (!res.ok) throw new Error(`edge responded ${res.status}`);
      payload = (await res.json()) as EdgePayload;
    } catch (err) {
      return {
        signals: {
          available: ok(false, 'client'),
          note: na<string>('unavailable', 'client', `edge unreachable: ${(err as Error)?.message ?? err}`),
        },
      };
    }

    const cf = payload.cf ?? {};
    const findings: Finding[] = [];

    // --- TLS ---------------------------------------------------------------
    const tlsVersion = cfString(cf, 'tlsVersion');
    const tlsCipher = cfString(cf, 'tlsCipher');
    const ciphersSha1 = cfString(cf, 'tlsClientCiphersSha1');
    const extensionsSha1 = cfString(cf, 'tlsClientExtensionsSha1');
    const clientHelloLength = cfString(cf, 'tlsClientHelloLength');
    const httpProtocol = cfString(cf, 'httpProtocol');

    // True JA4/JA3 when the paid Bot Management add-on is present.
    const bm = payload.botManagement;
    const trueJa4 = bm?.ja4 ?? null;
    const trueJa3 = bm?.ja3Hash ?? null;
    const hasBotManagement = !!bm;

    // Fallback: signature lookup on the ClientHello hashes (weaker than JA4 —
    // it discards ordering; see tlsSignatures.ts).
    const sigLookup = lookupTlsSignature(ciphersSha1 ?? undefined, extensionsSha1 ?? undefined);

    // A true JA4 string has the form t/q + 2 digits + 4 chars + 2 digits + 2
    // letters + 4 hex + '_' + 12 hex + '_' + 12 hex. We only *validate* the
    // shape here; we do not implement the algorithm (see licensing note in
    // tlsSignatures.ts).
    const ja4LooksValid = trueJa4 ? /^t13d\d{4}[a-z]{2}_[0-9a-f]{12}_[0-9a-f]{12}$/.test(trueJa4) : null;

    // --- transport ---------------------------------------------------------
    const quicRtt = cfNumber(cf, 'clientQuicRtt');
    const tcpRtt = cfNumber(cf, 'clientTcpRtt');
    const requestPriority = cfString(cf, 'requestPriority');
    const deliveryRate = (cf.edgeL4 as { deliveryRate?: number } | undefined)?.deliveryRate ?? null;

    // --- geo / network -----------------------------------------------------
    const asn = cfNumber(cf, 'asn');
    const asOrganization = cfString(cf, 'asOrganization');
    const country = cfString(cf, 'country');
    const city = cfString(cf, 'city');
    const region = cfString(cf, 'region');
    const continent = cfString(cf, 'continent');
    const colo = payload.colo;
    const timezone = cfString(cf, 'timezone');
    // The address the edge observed this request coming from. NOT hashed into
    // any part: it is the visitor's public IP and changes with their network.
    // Exposed so the DoH probe can cross-check it against the STUN reflexive
    // address, which is a genuine split-tunnel-VPN signal.
    const connectingIp = payload.edgeConnectingIp;

    // --- Accept-Encoding (the genuine original) ---------------------------
    const originalAcceptEncoding = payload.clientAcceptEncoding;
    // zstd support is a strong "recent Chrome/curl" signal.
    const supportsZstd = originalAcceptEncoding ? /zstd/i.test(originalAcceptEncoding) : null;

    // If we have a true JA4, great. If we only have the fallback signature and
    // it maps to a known automation library, note it. (Our table is empty, so
    // this rarely fires — kept for when rows are added.)
    if (sigLookup && /selenium|phantom|headless|python-requests|go-http/i.test(sigLookup.value.label)) {
      findings.push({
        id: 'edge.known-http-library-tls',
        severity: 'high',
        category: 'network',
        layer: 'tls',
        confidence: sigLookup.confidence,
        title: `TLS ClientHello matches ${sigLookup.value.label}`,
        detail:
          'The ClientHello cipher/extension hashes match a known HTTP client library rather than a browser. Browser automation tools built on raw HTTP libraries often leave the TLS stack untouched.',
        sources: [sigLookup.source, JA4_SOURCE],
        evidence: [`ciphersSha1=${ciphersSha1}`, `extensionsSha1=${extensionsSha1}`],
        scoreContribution: sigLookup.confidence === 'high' ? 40 : 15,
      });
    }

    return {
      signals: {
        available: ok(true, 'edge'),
        // TLS
        tlsVersion: ok(tlsVersion, 'edge'),
        tlsCipher: ok(tlsCipher, 'edge'),
        tlsClientCiphersSha1: ok(ciphersSha1, 'edge'),
        tlsClientExtensionsSha1: ok(extensionsSha1, 'edge'),
        tlsClientHelloLength: ok(clientHelloLength, 'edge'),
        // True JA4/JA3 and the signature lookup only exist with the paid Bot
        // Management add-on / a populated signature table. On a deployment
        // without them these were constant nulls, so they are reported only
        // when present. botManagementEnabled stays as the one status flag that
        // explains the card (and drives the export metadata).
        botManagementEnabled: ok(hasBotManagement, 'edge'),
        ...(trueJa4 ? { ja4: ok(trueJa4, 'edge') } : {}),
        ...(trueJa3 ? { ja3Hash: ok(trueJa3, 'edge') } : {}),
        ...(ja4LooksValid != null ? { ja4FormatValid: ok(ja4LooksValid, 'derived') } : {}),
        ...(sigLookup ? { clientHelloSignature: ok(sigLookup.value.label, 'derived') } : {}),
        // Transport
        httpProtocol: ok(httpProtocol, 'edge'),
        clientQuicRtt: ok(quicRtt, 'edge'),
        clientTcpRtt: ok(tcpRtt, 'edge'),
        requestPriority: ok(requestPriority, 'edge'),
        edgeDeliveryRate: ok(deliveryRate, 'edge'),
        // Network / geo
        asn: ok(asn, 'edge'),
        asOrganization: ok(asOrganization, 'edge'),
        country: ok(country, 'edge'),
        city: ok(city, 'edge'),
        region: ok(region, 'edge'),
        continent: ok(continent, 'edge'),
        colo: ok(colo, 'edge'),
        edgeTimezone: ok(timezone, 'edge'),
        edgeConnectingIp: ok(connectingIp, 'edge'),
        // Request characteristics
        clientAcceptEncoding: ok(originalAcceptEncoding, 'edge'),
        supportsZstd: ok(supportsZstd, 'derived'),
      },
      findings,
      parts: {
        tls: {
          // Hash only fields that are stable across connections in the SAME
          // browser. tlsVersion and tlsCipher are; ja4/ja3 are true
          // fingerprints when the paid Bot Management add-on supplies them.
          //
          // Deliberately EXCLUDED: clientHelloLength, tlsClientCiphersSha1 and
          // tlsClientExtensionsSha1. Verified against production that all
          // three change on every fresh connection (GREASE values plus
          // session-ticket / PSK state), which made this part — and therefore
          // the whole fingerprint hash — drift on every page load while the
          // stability e2e passed locally (it reuses one connection). They
          // remain signals, are still displayed, and still feed the signature
          // lookup; they are just not identity.
          tlsVersion,
          tlsCipher,
          ja4: trueJa4,
          ja3: trueJa3,
        },
      },
    };
  },
};

/**
 * Resolve the edge endpoint: same-origin /api/edge on Cloudflare Pages, or the
 * VITE_EDGE_ENDPOINT absolute URL when deployed to GitHub Pages.
 */
function resolveEndpoint(): string | null {
  if (typeof location === 'undefined') return null;
  const configured = import.meta.env?.VITE_EDGE_ENDPOINT as string | undefined;
  if (configured) {
    // Published so the e2e suite can distinguish "our own configured relay"
    // from a genuinely foreign third party. Read-only introspection of build
    // config, not a new behaviour.
    (window as unknown as { __FP_EDGE_ENDPOINT__?: string }).__FP_EDGE_ENDPOINT__ = configured;
    return configured;
  }
  // Default: same-origin relay (works on Cloudflare Pages where the Worker route
  // is bound to the same zone).
  return '/api/edge';
}

registerProbe(edgeProbe);
